import { z } from "zod/v4";
import { currencySchema } from "./common";

export const invoiceStatusSchema = z.enum(["draft", "sent", "partially_paid", "paid", "overdue", "cancelled"]);

export const paymentMethodSchema = z.enum(["stripe", "sepa", "manual"]);

export const feeIntervalSchema = z.enum(["monthly", "quarterly", "annual", "one_time"]);

export const lineItemResponseSchema = z.object({
	id: z.string(),
	description: z.string(),
	quantity: z.number(),
	unit_price_cents: z.number(),
	total_cents: z.number(),
	sort_order: z.number(),
});

export const paymentResponseSchema = z.object({
	id: z.string(),
	invoice_id: z.string(),
	amount_cents: z.number(),
	currency: currencySchema,
	method: paymentMethodSchema,
	stripe_payment_id: z.string().nullable().optional(),
	paid_at: z.coerce.date(),
	created_at: z.coerce.date(),
});

export const invoiceResponseSchema = z.object({
	id: z.string(),
	club_id: z.string(),
	member_id: z.string(),
	number: z.string(),
	status: invoiceStatusSchema,
	due_date: z.coerce.date(),
	total_cents: z.number(),
	currency: currencySchema,
	member_name: z.string(),
	member_email: z.string(),
	created_at: z.coerce.date(),
});

export const invoiceDetailResponseSchema = z.object({
	id: z.string(),
	club_id: z.string(),
	member_id: z.string(),
	number: z.string(),
	status: invoiceStatusSchema,
	due_date: z.coerce.date(),
	total_cents: z.number(),
	currency: currencySchema,
	member_name: z.string(),
	member_email: z.string(),
	line_items: z.array(lineItemResponseSchema),
	payments: z.array(paymentResponseSchema),
	created_at: z.coerce.date(),
	updated_at: z.coerce.date(),
});

export const membershipFeeResponseSchema = z.object({
	id: z.string(),
	club_id: z.string(),
	name: z.string(),
	amount_cents: z.number(),
	currency: currencySchema,
	interval: feeIntervalSchema,
	is_active: z.boolean(),
	// Visible to prospective (non-member) users — surfaced on the mobile join screen.
	is_public: z.boolean(),
	// Scheduler auto-bills + auto-reminds this fee; off = the club bills manually.
	auto_billing: z.boolean(),
	created_at: z.coerce.date(),
	updated_at: z.coerce.date(),
});

export const paymentDashboardResponseSchema = z.object({
	total_outstanding_cents: z.number(),
	total_overdue_cents: z.number(),
	collected_this_month_cents: z.number(),
	collected_this_year_cents: z.number(),
	currency: currencySchema,
	recent_payments: z.array(paymentResponseSchema),
});

export const markOverdueInvoicesResponseSchema = z.object({
	marked: z.number(),
});

export const createLineItemSchema = z.object({
	description: z.string().min(1, "Description is required"),
	quantity: z.number().int().min(1, "Quantity must be at least 1"),
	unit_price_cents: z.number().int().min(1, "Price must be positive"),
});

export const createInvoiceSchema = z.object({
	member_id: z.string().min(1, "Member is required"),
	due_date: z.date(),
	currency: currencySchema,
	line_items: z.array(createLineItemSchema).min(1, "At least one line item is required"),
});

export const updateInvoiceSchema = z.object({
	due_date: z.date().optional(),
	line_items: z.array(createLineItemSchema).optional(),
});

export const createPaymentSchema = z.object({
	invoice_id: z.string(),
	amount_cents: z.number().int().min(1, "Amount must be positive"),
	method: paymentMethodSchema,
});

export const createMembershipFeeSchema = z.object({
	name: z.string().min(1, "Name is required"),
	amount_cents: z.number().int().min(1, "Amount must be positive"),
	currency: currencySchema,
	interval: feeIntervalSchema,
	is_public: z.boolean(),
	auto_billing: z.boolean(),
});

export const updateMembershipFeeSchema = z.object({
	name: z.string().min(1).optional(),
	amount_cents: z.number().int().min(1).optional(),
	interval: feeIntervalSchema.optional(),
	is_active: z.boolean().optional(),
	is_public: z.boolean().optional(),
	auto_billing: z.boolean().optional(),
});

export const creditNoteResponseSchema = z.object({
	id: z.string(),
	invoice_id: z.string(),
	club_id: z.string(),
	reason: z.string(),
	amount_cents: z.number(),
	is_refund: z.boolean(),
	stripe_refund_id: z.string().nullable().optional(),
	created_at: z.coerce.date(),
});

export const createCreditNoteSchema = z.object({
	reason: z.string().min(1, "Reason is required"),
	amount_cents: z.number().int().min(1, "Amount must be positive"),
	is_refund: z.boolean(),
});
